'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Course } from '@udemy-v2/browse-course';
import { useI18n } from '@udemy-v2/i18n';
import { Loader } from '@udemy-v2/react-reveal-components';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import { fetchCourseData } from '@/app/lib/course/course-fetcher';
import { CourseLandingPageWithProviders } from '@/features/course-landing-page/course-landing-page-with-providers.react-component';
import type { TrackedCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { CourseAccessRestriction } from './constants';
import { CourseAccessError } from './course-access-error';
import { InviteOnlyCourse } from './invite-only-course.react-component';
import { PasswordProtectedCourse } from './password-protected-course.react-component';
import { UnavailableCourse } from './unavailable-course.react-component';
import styles from './limited-access-clp.module.scss';
interface LimitedAccessCLPProps {
  /** Restriction reported by the GraphQL `ACCESS_RESTRICTED` extension. */
  restriction: CourseAccessRestriction;
  /** Course landing URL — used by the credentialed client re-fetch. */
  courseLandingUrl: string;
  /** Course id used by `INVITE_ONLY` to request an invitation from the monolith. */
  courseId?: string;
  /** Course title to thread into the password prompt when known. */
  courseTitle?: string;
  /** Server-supplied error message for a wrong password submission. */
  passwordError?: string;
  /** topic id from the ACCESS_RESTRICTED extension, threaded to `UnavailableCourse`. */
  topicId?: string;
  /** category id from the ACCESS_RESTRICTED extension, threaded to `UnavailableCourse`. */
  categoryId?: string;
  /** subcategory id from the ACCESS_RESTRICTED extension, threaded to `UnavailableCourse`. */
  subcategoryId?: string;
  /**
   * When `true`, the component issues a credentialed GraphQL re-fetch on
   * mount for authenticated users so that a logged-in user with access via
   * enrollment or invitation (and no `?password` / `?xCourseControlId`
   * param) can still see the full CLP. Set this from the ISR bare-CLP route
   * (whose cached HTML was built anonymously); leave it `false` on the
   * `/access` SSR variant, which already fetched per-user.
   */
  enableClientRefetch?: boolean;
}

/**
 * Single source of truth for limited-access course rendering. Both the ISR
 * bare-CLP route and the SSR `/access` variant delegate to this component.
 *
 * - `UNAVAILABLE` renders `UnavailableCourse` (a warning banner with a
 *   subtitle keyed off the viewer's enrollment status).
 * - `UNAVAILABLE_IN_YOUR_ORGANIZATION` is redirected server-side by the proxy before this
 *   renders, so it only reaches here if that lookup failed; it renders the same UI as
 *   `UNAVAILABLE`.
 * - `PASSWORD_PROTECTED` and `INVITE_ONLY` render the restricted UI by
 *   default. When mounted in a context that opts into it via
 *   `enableClientRefetch`, the component issues a credentialed GraphQL
 *   re-fetch on hydration; on success it swaps in the full CLP via
 *   `CourseLandingPageWithProviders`. When the re-fetch is itself access
 *   restricted (`CourseAccessError`) its restriction replaces the one from
 *   the anonymous ISR render: the cached HTML can only say `UNAVAILABLE` for
 *   an organization course, while the viewer's session may be allowed to
 *   unlock it (e.g. an org member who just needs the password). On any other
 *   failure it falls back to the restriction passed in props.
 *
 *   While the fetch is in flight a full-page `<Loader size="xxlarge" />`
 *   renders. The fetch fires unconditionally for the `enableClientRefetch`
 *   path (not gated on `useCommonData`) so that the loader leads continuously
 *   up to the result without flashing the restricted UI between hydration and
 *   the async commonData update.
 *
 * The app sets `appConfig.useDefaultLayout = false`, so the root layout
 * intentionally omits Header/Footer — this component renders them to keep
 * the limited-access flows visually consistent.
 */
export function LimitedAccessCLP({
  restriction,
  courseLandingUrl,
  courseId,
  courseTitle,
  passwordError,
  topicId,
  categoryId,
  subcategoryId,
  enableClientRefetch = false
}: LimitedAccessCLPProps) {
  const {
    locale
  } = useI18n();

  // `useSyncExternalStore` returns `false` during SSR and the initial
  // hydration render, then `true` after — the same pattern used in
  // `fireworks.tsx`. This lets us derive the "is loading" UI state from
  // hydration + refetch-enabled rather than calling `setState`
  // synchronously inside an effect (which trips
  // react-hooks/set-state-in-effect) and avoids hydration mismatches
  // between the anonymous SSR/ISR render and the credentialed client run.
  const hasHydrated = useSyncExternalStore(
  // No-op subscribe; the value never changes after hydration.
  () => () => {}, () => true, () => false);

  // Refetch unconditionally for the `enableClientRefetch` path, rather
  // than gating on `commonData?.user`. The layout loads user-specific
  // commonData via an async `<script>` tag, so `useCommonData()` returns
  // the build-time anonymous snapshot during the window between
  // hydration and the script's execution. Gating on `isLoggedIn` there
  // makes us briefly transition to the restricted UI before
  // transitioning back to the loader once commonData settles — the
  // flash you'd see between the initial paint and the refetch.
  // Anonymous viewers of restricted courses pay one wasted GraphQL
  // roundtrip (which returns the same access error the server-side
  // fetch already got) for a continuous loader UX up to the result.
  const shouldRefetch = enableClientRefetch;
  const [course, setCourse] = useState<Course | null>(null);
  // Tracks whether the refetch has settled (success or failure). Without
  // this, a failed refetch would leave the loader visible forever — we'd
  // have no way to distinguish "fetch still in flight" from "fetch
  // resolved with no course". Kept as a single-purpose boolean rather
  // than collapsed into the `course` state to avoid a sentinel value on
  // a variable whose name promises a course.
  const [refetchSettled, setRefetchSettled] = useState(false);
  // Set when the credentialed refetch is itself access restricted. It takes
  // precedence over the props, which come from the anonymous ISR render.
  const [refetchAccessError, setRefetchAccessError] = useState<CourseAccessError | null>(null);
  useEffect(() => {
    if (!shouldRefetch) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const refetched = await fetchCourseData(courseLandingUrl, new Headers(), locale);
        if (!cancelled) {
          setCourse(refetched);
        }
      } catch (error) {
        // A CourseAccessError carries the restriction for this
        // viewer's session, which is more accurate than the anonymous
        // one the page was rendered with. Any other error falls
        // through to the restriction from props via the settle flag.
        if (!cancelled && error instanceof CourseAccessError) {
          setRefetchAccessError(error);
        }
      } finally {
        if (!cancelled) {
          setRefetchSettled(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldRefetch, courseLandingUrl, locale]);
  const effective = {
    restriction: refetchAccessError?.restriction ?? restriction,
    courseId: refetchAccessError?.courseId ?? courseId,
    courseTitle: refetchAccessError?.courseTitle ?? courseTitle,
    passwordError: refetchAccessError?.passwordError ?? passwordError,
    topicId: refetchAccessError?.topicId ?? topicId,
    categoryId: refetchAccessError?.categoryId ?? categoryId,
    subcategoryId: refetchAccessError?.subcategoryId ?? subcategoryId
  };

  // Lead with the loader for everyone before hydration (matches SSR/ISR
  // where we can't know about the user yet) and for the in-flight window
  // of a logged-in user's refetch. This avoids flashing the restricted UI
  // before the loader for a logged-in user with access.
  const isLoading = !hasHydrated || shouldRefetch && !refetchSettled;

  // CourseLandingPage renders its own Header and Footer, so skip the wrapper.
  if (course) {
    return <CourseLandingPageWithProviders course={course as TrackedCourse} />;
  }
  let body;
  if (isLoading) {
    body = <div className={styles['loader']}>
                <Loader size="xxlarge" />
            </div>;
  } else {
    switch (effective.restriction) {
      case CourseAccessRestriction.PASSWORD_PROTECTED:
        body = <PasswordProtectedCourse courseTitle={effective.courseTitle} error={effective.passwordError} />;
        break;
      case CourseAccessRestriction.INVITE_ONLY:
        body = <InviteOnlyCourse courseId={effective.courseId} />;
        break;
      case CourseAccessRestriction.UNAVAILABLE_IN_YOUR_ORGANIZATION:
      case CourseAccessRestriction.UNAVAILABLE:
        body = <UnavailableCourse courseId={effective.courseId} courseLandingUrl={courseLandingUrl} topicId={effective.topicId} categoryId={effective.categoryId} subcategoryId={effective.subcategoryId} />;
        break;
    }
  }
  return <>
            <Header />
            {body}
            <Footer />
        </>;
}